import { msg } from "@lingui/core/macro";
import * as Sentry from "@sentry/react";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  Params,
  RouteObject,
} from "react-router-dom";

import { PermissionBit, UserFeatureFlag } from "../api";
import { clinicalStudies } from "../constants/enum-to-text";
import { AccountRoute } from "../features/account/account-route";
import { AddPatientRoute } from "../features/add-patient/add-patient-route";
import { AdminRoute } from "../features/admin/admin-route";
import { AnalyticsRoute } from "../features/admin/analytics/analytics-route";
import { ClinicsRoute } from "../features/admin/clinics/clinics-route";
import { MonitoringManagementRoute } from "../features/admin/monitoring-management/monitoring-management-route";
import { PhysiciansRoute } from "../features/admin/physicians/physicians-route";
import { ConsentsFormRoute } from "../features/admin/settings/consents-form-route";
import { ConsentsRoute } from "../features/admin/settings/consents-route";
import { MonitoringSettingsRoute } from "../features/admin/settings/monitoring-settings-route";
import { QuestionnaireSettingsRoute } from "../features/admin/settings/questionnaire-settings-route";
import { UsersRoute } from "../features/admin/users/users-route";
import { AuditRoomRoute } from "../features/audit-room/audit-room-route";
import { LoginRoute } from "../features/auth/login/login-route";
import { TokenLoginRoute } from "../features/auth/login/token-login-route";
import { ForgotPasswordRoute } from "../features/auth/password/forgot-password-route";
import { ResetPasswordRoute } from "../features/auth/password/reset-password-route";
import { CalendarRoute } from "../features/calendar/calendar-route";
import { DefinitionRoute } from "../features/clinical-studies/definition/definition-route";
import { OverviewRoute } from "../features/clinical-studies/overview-route";
import { StudiesListRoute } from "../features/clinical-studies/studies-list-route";
import { prettyVisitType } from "../features/clinical-studies/utils";
import { DailyMonitoringRoute } from "../features/daily-monitoring/daily-monitoring-route";
import { ExtractionQueueRoute } from "../features/extraction-queue/extraction-queue-route";
import { MedWaitingRoomRoute } from "../features/med-waiting-room/med-waiting-room-route";
import { AFibTable } from "../features/patient/afib/a-fib-table";
import { AlertsTable } from "../features/patient/alerts/alerts-table";
import { AddAnyDataTable } from "../features/patient/any-data/add-any-data-table";
import { ClinicalStudiesRoute } from "../features/patient/clinical-studies/clinical-studies-route";
import { EnrollmentDetailsRoute } from "../features/patient/clinical-studies/enrollment-details-route";
import { VisitRoute } from "../features/patient/clinical-studies/visit-route";
import { AddDiagsTable } from "../features/patient/diagnoses/add-diags-table";
import { DiagnosesRoute } from "../features/patient/diagnoses/diagnoses-route";
import { DRITable } from "../features/patient/dri/dri-table";
import { ECGTable } from "../features/patient/ecg/ecg-table";
import { EventsTable } from "../features/patient/events/events-table";
import { AddExamsTable } from "../features/patient/exams/add-exams-table";
import { AddRHCTable } from "../features/patient/exams/add-rhc-table";
import { ExamsTable } from "../features/patient/exams/exams-table";
import { HealthPro } from "../features/patient/health-pro/health-pro";
import { LabsTable } from "../features/patient/labs/labs-table";
import { LogsTable } from "../features/patient/logs/logs-table";
import { LVFPTable } from "../features/patient/lvfp/lvfp-table";
import { MeasurementsRoute } from "../features/patient/measurements/measurements-route";
import { AddMedicalTable } from "../features/patient/medical/add-medical-table";
import { BasicMedicalRoute } from "../features/patient/medical/basic-medical-route";
import { MedicationsTable } from "../features/patient/medications/medications-table";
import { PatientRoute } from "../features/patient/patient-route";
import { PatientPhysiciansRoute } from "../features/patient/physicians/patient-physicians-route";
import { AddPPGForm } from "../features/patient/ppg/add-ppg-form";
import { PPGTable } from "../features/patient/ppg/ppg-table";
import { ProfileRoute } from "../features/patient/profile/profile-route";
import { QuestionnaireRoute } from "../features/patient/questionnaire/questionnaire-route";
import { ReportsRoute } from "../features/patient/reports/reports-route";
import { SettingsRoute } from "../features/patient/settings/settings-route";
import { SymptomsRoute } from "../features/patient/symptoms/symptoms-route";
import { ThresholdsTable } from "../features/patient/thresholds/thresholds-table";
import { VitalsTable } from "../features/patient/vitals/vitals-table";
import { PatientsRoute } from "../features/patients/patients-route";
import { TechWaitingRoomRoute } from "../features/tech-waiting-room/tech-waiting-room-route";
import { assertStudyAcronym } from "../utils/clinical-studies";
import { AppModel } from "./app-model";
import { MainLayout } from "./layout/main-layout";
import { pageTitles } from "./page-titles";
import {
  RAW_ALIAS_SEGMENTS,
  RawAliasRedirect,
  RawIndexRedirect,
} from "./raw-data";
import { RouteAccess } from "./route-access";
import { RouteErrorBoundary } from "./route-error-boundary";
import { RouterRoot } from "./router-root";

export function createRouter(app: AppModel) {
  return Sentry.wrapCreateBrowserRouterV7(createBrowserRouter)(
    createRoutes(app),
  );
}

/**
 * The app's route tree, separate from the router so that other kinds of
 * router (a memory router, say) can mount it too.
 */
export function createRoutes(app: AppModel): RouteObject[] {
  return [
    {
      element: <RouterRoot />,
      errorElement: <RouteErrorBoundary />,
      children: [
        {
          path: "/login",
          element: <LoginRoute app={app} />,
        },
        {
          path: "/forgot-password",
          element: <ForgotPasswordRoute app={app} />,
        },
        {
          path: "/reset-password",
          element: <ResetPasswordRoute mode="reset" />,
        },
        {
          path: "/set-password",
          element: <ResetPasswordRoute mode="set" />,
        },
        {
          path: "/token-login",
          element: <TokenLoginRoute app={app} />,
        },
        {
          element: <MainLayout app={app} />,
          children: [
            {
              path: "/",
              element: <PatientsRoute api={app.seerlinqApi} app={app} />,
              handle: { crumb: pageTitles.patients },
            },
            {
              path: "/patient/:id/*",
              element: <PatientRoute app={app} />,
              handle: {
                crumb: (params: Params) => msg`Patient #${params.id ?? ""}`,
              },
              children: [
                {
                  path: "",
                  element: <ProfileRoute app={app} />,
                  handle: { crumb: pageTitles.trends },
                },
                {
                  path: "measurements",
                  element: <MeasurementsRoute app={app} />,
                  handle: { crumb: pageTitles.measurements },
                },
                {
                  path: "settings",
                  element: <SettingsRoute app={app} />,
                  handle: { crumb: pageTitles.settings },
                },
                {
                  path: "alerts",
                  handle: { crumb: pageTitles.alerts },
                  children: [
                    {
                      index: true,
                      element: <AlertsTable app={app} />,
                    },
                    {
                      path: "thresholds",
                      element: <ThresholdsTable app={app} />,
                      handle: { crumb: pageTitles.thresholds },
                    },
                  ],
                },
                {
                  path: "physicians",
                  element: <PatientPhysiciansRoute app={app} />,
                  handle: { crumb: pageTitles.physicians },
                },
                {
                  path: "any",
                  element: <AddAnyDataTable app={app} />,
                  handle: { crumb: pageTitles.addAnyData },
                },
                {
                  path: "basics",
                  element: <BasicMedicalRoute app={app} />,
                  handle: { crumb: pageTitles.basicMedicalData },
                },
                {
                  path: "diags",
                  handle: { crumb: pageTitles.comorbidities },
                  children: [
                    {
                      index: true,
                      element: <DiagnosesRoute app={app} />,
                    },
                    {
                      path: "add",
                      element: <AddDiagsTable app={app} />,
                      handle: { crumb: msg`Add`, backButton: true },
                    },
                  ],
                },
                {
                  path: "events",
                  element: <EventsTable app={app} />,
                  handle: { crumb: pageTitles.events },
                },
                {
                  path: "exams",
                  handle: { crumb: pageTitles.exams },
                  children: [
                    {
                      index: true,
                      element: <ExamsTable app={app} />,
                    },
                    {
                      path: "add-other",
                      element: <AddExamsTable app={app} variant="exams" />,
                      handle: { crumb: msg`Add other`, backButton: true },
                    },
                    {
                      path: "add-echo",
                      element: <AddExamsTable app={app} variant="echo" />,
                      handle: { crumb: msg`Add ECHO`, backButton: true },
                    },
                    {
                      path: "add-rhc",
                      element: <AddRHCTable app={app} />,
                      handle: { crumb: msg`Add RHC`, backButton: true },
                    },
                    {
                      path: "add-lhc",
                      element: <AddExamsTable app={app} variant="lhc" />,
                      handle: { crumb: msg`Add LHC`, backButton: true },
                    },
                  ],
                },
                {
                  path: "healthpro",
                  element: <HealthPro app={app} />,
                  handle: { crumb: pageTitles.healthProReporting },
                },
                {
                  path: "labs",
                  handle: { crumb: pageTitles.labs },
                  children: [
                    {
                      index: true,
                      element: <LabsTable app={app} />,
                    },
                    {
                      path: "add",
                      element: <AddMedicalTable app={app} />,
                      handle: { crumb: msg`Add`, backButton: true },
                    },
                  ],
                },
                {
                  path: "medications",
                  element: <MedicationsTable app={app} />,
                  handle: { crumb: pageTitles.medications },
                },
                {
                  path: "logs",
                  element: <LogsTable app={app} />,
                  handle: { crumb: pageTitles.logs },
                },
                {
                  path: "reports",
                  element: <ReportsRoute app={app} />,
                  handle: { crumb: pageTitles.medicalReports },
                },
                {
                  path: "questionnaire",
                  element: <QuestionnaireRoute app={app} />,
                  handle: { crumb: pageTitles.questionnaires },
                },
                {
                  path: "clinical-studies",
                  element: (
                    <RouteAccess
                      app={app}
                      permission={PermissionBit.CLINICAL_STUDIES}
                    >
                      <Outlet />
                    </RouteAccess>
                  ),
                  handle: { crumb: pageTitles.clinicalStudies },
                  children: [
                    {
                      path: "",
                      element: <ClinicalStudiesRoute app={app} />,
                    },
                    {
                      path: ":study",
                      handle: {
                        crumb: (params: Params) =>
                          clinicalStudies[assertStudyAcronym(params.study)],
                      },
                      children: [
                        {
                          index: true,
                          element: <EnrollmentDetailsRoute app={app} />,
                        },
                        {
                          path: ":visit",
                          element: <VisitRoute app={app} />,
                          handle: {
                            crumb: (params: Params) =>
                              prettyVisitType(params.visit as string),
                          },
                        },
                      ],
                    },
                  ],
                },
                {
                  path: "raw",
                  element: (
                    <RouteAccess
                      app={app}
                      permission={PermissionBit.GROUP_ADMIN_ROUTERS}
                    >
                      <Outlet />
                    </RouteAccess>
                  ),
                  handle: { crumb: pageTitles.rawData },
                  children: [
                    // Clicking the nav parent's label navigates here; land on
                    // whichever child sits at the top of this user's menu.
                    {
                      index: true,
                      element: <RawIndexRedirect app={app} />,
                    },
                    {
                      path: "lvfp",
                      element: <LVFPTable app={app} />,
                      handle: { crumb: pageTitles.lvfp },
                    },
                    {
                      path: "dri",
                      element: <DRITable app={app} />,
                      handle: { crumb: pageTitles.dri },
                    },
                    {
                      path: "afib",
                      element: <AFibTable app={app} />,
                      handle: { crumb: pageTitles.afib },
                    },
                    {
                      path: "vitals",
                      element: <VitalsTable app={app} />,
                      handle: { crumb: pageTitles.vitals },
                    },
                    {
                      path: "symptoms",
                      element: <SymptomsRoute app={app} />,
                      handle: { crumb: pageTitles.symptoms },
                    },
                    {
                      path: "ppg",
                      handle: { crumb: pageTitles.ppgData },
                      children: [
                        {
                          index: true,
                          element: <PPGTable app={app} />,
                        },
                        {
                          path: "add",
                          element: <AddPPGForm app={app} />,
                          handle: { crumb: msg`Add`, backButton: true },
                        },
                      ],
                    },
                    {
                      path: "ecg",
                      element: <ECGTable app={app} />,
                      handle: { crumb: pageTitles.ecgData },
                    },
                  ],
                },
                // Transitional aliases for the pre-#743 flat URLs. A splat
                // matches the bare segment too, so `ppg/*` covers `/ppg` and
                // `/ppg/add` alike. Deliberately ungated: a non-admin is
                // forwarded and then denied by the `raw` gate above, keeping
                // the permission check in one place.
                ...RAW_ALIAS_SEGMENTS.map((segment) => ({
                  path: `${segment}/*`,
                  element: <RawAliasRedirect />,
                })),
              ],
            },
            {
              path: "/add-patient",
              element: (
                <RouteAccess
                  app={app}
                  permission={({ seerlinqApi }) => seerlinqApi.canIAddPatients}
                >
                  <AddPatientRoute app={app} />
                </RouteAccess>
              ),
              handle: { crumb: pageTitles.addNewPatient },
            },
            {
              path: "/extraction-queue",
              element: (
                <RouteAccess
                  app={app}
                  permission={PermissionBit.GROUP_MONITORING_ROUTERS}
                >
                  <ExtractionQueueRoute app={app} />
                </RouteAccess>
              ),
              handle: { crumb: pageTitles.extractionQueue },
            },
            {
              path: "/med-waiting-room",
              element: <MedWaitingRoomRoute app={app} />,
              handle: { crumb: pageTitles.medWaitingRoom },
            },
            {
              path: "/tech-waiting-room",
              element: <TechWaitingRoomRoute app={app} />,
              handle: { crumb: pageTitles.techWaitingRoom },
            },
            {
              path: "/audit-room",
              element: <AuditRoomRoute app={app} />,
              handle: { crumb: pageTitles.auditRoom },
            },
            {
              path: "/account",
              element: <AccountRoute api={app.seerlinqApi} />,
              handle: { crumb: msg`Account` },
            },
            {
              path: "/daily-monitoring",
              element: (
                <RouteAccess
                  app={app}
                  permission={PermissionBit.GROUP_MONITORING_ROUTERS}
                  featureFlag={UserFeatureFlag.fe_daily_monitoring_dashboard}
                >
                  <DailyMonitoringRoute app={app} />
                </RouteAccess>
              ),
              handle: { crumb: pageTitles.dailyMonitoring },
            },
            {
              path: "/calendar",
              element: (
                <RouteAccess
                  app={app}
                  permission={PermissionBit.CLINICAL_STUDIES}
                >
                  <CalendarRoute />
                </RouteAccess>
              ),
              handle: { crumb: pageTitles.calendar },
            },
            {
              path: "/admin/*",
              element: <AdminRoute app={app} />,
              handle: { crumb: pageTitles.clvManagement },
              children: [
                {
                  path: "",
                  element: <Navigate to="/admin/clinics" replace />,
                },
                {
                  path: "clinics",
                  handle: { crumb: pageTitles.clinics },
                  element: <ClinicsRoute app={app} />,
                },
                {
                  path: "physicians",
                  handle: { crumb: pageTitles.physicians },
                  element: <PhysiciansRoute app={app} />,
                },
                {
                  path: "users",
                  handle: { crumb: pageTitles.users },
                  element: <UsersRoute app={app} />,
                },
                {
                  path: "monitoring-management",
                  element: <MonitoringManagementRoute app={app} />,
                  handle: { crumb: pageTitles.monitoringManagement },
                },
                {
                  path: "analytics",
                  element: <AnalyticsRoute api={app.seerlinqApi} />,
                  handle: { crumb: pageTitles.analytics },
                },
                {
                  path: "settings",
                  handle: { crumb: pageTitles.settings },
                  children: [
                    {
                      index: true,
                      element: (
                        <Navigate to="/admin/settings/monitoring" replace />
                      ),
                    },
                    {
                      path: "monitoring",
                      handle: { crumb: pageTitles.monitoring },
                      element: <MonitoringSettingsRoute app={app} />,
                    },
                    {
                      path: "questionnaires",
                      handle: { crumb: pageTitles.questionnaires },
                      element: <QuestionnaireSettingsRoute app={app} />,
                    },
                    {
                      path: "consents",
                      handle: { crumb: pageTitles.consents },
                      children: [
                        {
                          index: true,
                          element: <ConsentsRoute app={app} />,
                        },
                        {
                          path: "add",
                          element: <ConsentsFormRoute app={app} />,
                          handle: { crumb: msg`Add`, backButton: true },
                        },
                        {
                          path: ":uuid",
                          element: <ConsentsFormRoute app={app} />,
                          handle: { crumb: msg`View`, backButton: true },
                        },
                      ],
                    },
                  ],
                },
              ],
            },
            {
              element: (
                <RouteAccess
                  app={app}
                  permission={PermissionBit.CLINICAL_STUDIES}
                >
                  <Outlet />
                </RouteAccess>
              ),
              children: [
                {
                  path: "/clinical-studies",
                  element: <StudiesListRoute />,
                  handle: { crumb: pageTitles.clinicalStudies },
                },
                {
                  path: "/clinical-studies/:name/*",
                  handle: {
                    crumb: (params: Params) =>
                      clinicalStudies[assertStudyAcronym(params.name)],
                  },
                  children: [
                    {
                      path: "",
                      element: <DefinitionRoute api={app.seerlinqApi} />,
                    },
                    {
                      path: "overview",
                      element: <OverviewRoute api={app.seerlinqApi} />,
                      handle: { crumb: pageTitles.overview },
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
  ];
}
